import { z } from 'zod';
import { decimalString } from './common';
import { cabinetPieceKind, MAX_CABINET_PIECES } from './project-cabinets';
import { materialListItem } from './materials';

/**
 * Shared pricing-engine schemas.
 *
 * PRICING_ENGINE_PLAN F.4 — preview endpoints exposed at:
 *   POST /api/v1/pricing/{cabinet,panel,countertop,drawer,element,lighting}/preview
 */

const positiveDecimal = decimalString.refine((v) => parseFloat(v) > 0, {
  message: 'Must be greater than 0',
});
const positiveIntegerDecimal = decimalString.refine(
  (v) => Number.isInteger(Number(v)) && Number(v) > 0,
  {
    message: 'Must be a positive integer',
  },
);
const nonNegativeDecimal = decimalString.refine((v) => parseFloat(v) >= 0, {
  message: 'Must be ≥ 0',
});
const dimMm = z.number().int().min(1).max(100_000);

export const pricingMode = z.enum(['MANUAL', 'AUTO']);
export type PricingModeValue = z.infer<typeof pricingMode>;

export const drawerModelKind = z.enum(['BOX', 'BOX_INTERIOR', 'INTERIOR', 'STANDARD']);
export type DrawerModelKindValue = z.infer<typeof drawerModelKind>;

export const joinTypeKind = z.enum(['EUROSCREW', 'DEMONTABIL', 'CLAMEX', 'MITER_45', 'NONE']);
export type JoinTypeKindValue = z.infer<typeof joinTypeKind>;

export const hboxKind = z.enum([
  'HBox_70',
  'HBox_85',
  'HBox_106',
  'HBox_146',
  'HBox_186',
  'HBox_226',
  'HBox_266',
  'HBox_306',
]);
export type HBoxKindValue = z.infer<typeof hboxKind>;

export const legacyDrawerHBoxKind = z.enum([
  '39',
  '63',
  '101',
  '148',
  '184',
  '199',
  '212',
  'N',
  'M',
  'K',
  'C',
  'F',
  'E',
  'D',
]);
export type LegacyDrawerHBoxKindValue = z.infer<typeof legacyDrawerHBoxKind>;

// ---------------------------------------------------------------------------
// Breakdown shape (subset surfaced over HTTP — matches calculator types.ts)
// ---------------------------------------------------------------------------

export const breakdownComponent = z
  .object({
    kind: z.string(),
    label: z.string(),
    amount: decimalString,
    /**
     * The markup this component's material was priced at, when it has one.
     * `amount` already includes it — there is no row-level coefficient left to
     * apply, so the panel shows the figure per line.
     */
    coefficient: decimalString.optional(),
  })
  .passthrough();

export const breakdownSchema = z.object({
  components: z.array(breakdownComponent),
  /** Sum of the component amounts, each already carrying its own markup. */
  subtotalNet: decimalString,
  /**
   * The same per-unit sum at catalog cost, every per-use markup held out
   * (glass sanding and labour stay in). Absent on breakdowns stored before the
   * field existed; a recompute fills it.
   */
  subtotalBaseNet: decimalString.optional(),
  /**
   * The one markup describing the whole row, where such a thing exists —
   * elements and lighting, which carry no material for a coefficient to sit on.
   */
  coefficientApplied: decimalString.optional(),
  glassSandingApplied: z.boolean().optional(),
  totalNet: decimalString,
  currency: z.literal('RON'),
});
export type BreakdownDto = z.infer<typeof breakdownSchema>;

export const previewResponseSchema = z.object({
  breakdown: breakdownSchema,
  totalNet: decimalString,
  currency: z.literal('RON'),
});
export type PreviewResponse = z.infer<typeof previewResponseSchema>;

// ---------------------------------------------------------------------------
// Pricing constants (GET /pricing/constants)
// ---------------------------------------------------------------------------

/**
 * The engine constants a client needs in order to EXPLAIN a price rather than
 * to compute one.
 *
 * Deliberately narrower than `SystemSettings`: that endpoint is gated behind
 * SETTINGS_READ (admins only), while anyone who can edit a line item has to be
 * able to read why a glass-sanded front costs 30% more than the board under it.
 * Only multipliers the UI quotes back to the user belong here — labour rates and
 * per-operation minutes stay admin-only.
 */
export const pricingConstantsSchema = z.object({
  glassSandingMarkup: decimalString,
});
export type PricingConstantsDto = z.infer<typeof pricingConstantsSchema>;

// ---------------------------------------------------------------------------
// Per-item preview request schemas
// ---------------------------------------------------------------------------

export const previewPanelRequestSchema = z.object({
  widthMm: dimMm,
  heightMm: dimMm,
  materialId: z.string().min(1),
  edgeId: z.string().min(1).optional().nullable(),
  doubled: z.boolean().default(false),
  quantity: positiveIntegerDecimal.default('1'),
  coefficient: positiveDecimal.default('2.5'),
});
export type PreviewPanelRequest = z.infer<typeof previewPanelRequestSchema>;

export const previewCountertopRequestSchema = z.object({
  widthMm: dimMm.default(600),
  depthMm: dimMm.default(560),
  materialId: z.string().min(1),
  edgeId: z.string().min(1).optional().nullable(),
  doubled: z.boolean().default(false),
  quantity: positiveIntegerDecimal.default('1'),
  coefficient: positiveDecimal.default('2.5'),
});
export type PreviewCountertopRequest = z.infer<typeof previewCountertopRequestSchema>;

export const previewDrawerRequestSchema = z
  .object({
    hbox: hboxKind.optional(),
    hBox: z.string().optional(),
    heightMm: dimMm,
    widthMm: dimMm,
    depthMm: dimMm,
    model: drawerModelKind,
    carcassMaterialId: z.string().min(1),
    carcassEdgeId: z.string().min(1).optional().nullable(),
    frontMaterialId: z.string().min(1).optional().nullable(),
    frontEdgeId: z.string().min(1).optional().nullable(),
    drawerSystemVariantId: z.string().min(1),
    quantity: positiveDecimal.default('1'),
    /** Markup for the box board (sides, back, bottom). */
    coefficient: positiveDecimal.default('1.00'),
    /** Markup for the front board. Falls back to `coefficient` when absent. */
    frontCoefficient: positiveDecimal.optional(),
    /**
     * Markup for the bought-in slide system. Falls back to `coefficient` when
     * absent, which is what priced it before the drawer carried its own number.
     */
    systemCoefficient: positiveDecimal.optional(),
  })
  .refine((v) => Boolean(v.hbox || v.hBox), {
    message: 'hbox or hBox is required',
    path: ['hBox'],
  });
export type PreviewDrawerRequest = z.infer<typeof previewDrawerRequestSchema>;

export const previewElementRequestSchema = z.object({
  // Each element component is a Hardware catalog row + qty + the markup for
  // that use of it (Σ unitPrice·qty·coefficient). A line that names no
  // coefficient falls back to the element-wide one below, which is what priced
  // the whole basket before the markup moved onto each line.
  hardwareItems: z
    .array(
      z.object({
        hardwareId: z.string().min(1),
        qty: positiveDecimal,
        coefficient: nonNegativeDecimal.optional(),
      }),
    )
    .min(1),
  quantity: positiveDecimal.default('1'),
  coefficient: nonNegativeDecimal.default('1.00'),
});
export type PreviewElementRequest = z.infer<typeof previewElementRequestSchema>;

export const previewLightingRequestSchema = z.object({
  lightingCatalogId: z.string().min(1),
  quantity: positiveDecimal,
  lengthM: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .optional(),
  coefficient: positiveDecimal.default('1.8'),
});
export type PreviewLightingRequest = z.infer<typeof previewLightingRequestSchema>;

export const previewCabinetHardwareItemSchema = z.object({
  name: z.string().optional(),
  quantity: positiveDecimal,
  lengthM: nonNegativeDecimal.optional(),
  coefficient: nonNegativeDecimal.default('1.00'),
  unitPriceNet: nonNegativeDecimal,
});
export type PreviewCabinetHardwareItem = z.infer<typeof previewCabinetHardwareItemSchema>;

export const previewCabinetRequestSchema = z.object({
  widthMm: dimMm,
  heightMm: dimMm,
  depthMm: dimMm,
  carcassMaterialId: z.string().min(1),
  carcassEdgeId: z.string().min(1).optional().nullable(),
  frontMaterialId: z.string().min(1).optional().nullable(),
  frontEdgeId: z.string().min(1).optional().nullable(),
  backMaterialId: z.string().min(1).optional().nullable(),
  backEdgeId: z.string().min(1).optional().nullable(),
  backHasEdge: z.boolean().default(true),
  carcassDoubled: z.boolean().default(false),
  carcassExteriorFront: z.boolean().default(false),
  shelvesCount: z.number().int().min(0).default(0),
  shelvesDoubled: z.boolean().default(false),
  dividersCount: z.number().int().min(0).default(0),
  dividersDoubled: z.boolean().default(false),
  hardware: z.array(previewCabinetHardwareItemSchema).default([]),
  joinType: joinTypeKind,
  quantity: positiveDecimal.default('1'),
  // One markup per board rather than one per cabinet: a cabinet can mix a 2.5
  // carcass with a 1.0 painted front. The carcass value also covers shelves and
  // dividers, which are cut from that board.
  carcassCoefficient: nonNegativeDecimal.default('1.00'),
  frontCoefficient: nonNegativeDecimal.default('1.00'),
  backCoefficient: nonNegativeDecimal.default('1.00'),
});
export type PreviewCabinetRequest = z.infer<typeof previewCabinetRequestSchema>;

// ---------------------------------------------------------------------------
// Cut-list ("Pe piese") cabinet preview
// ---------------------------------------------------------------------------
//
// A separate request rather than a widened `previewCabinetRequestSchema`: that
// one hard-requires widthMm/heightMm/depthMm, carcassMaterialId and joinType,
// none of which apply here. Relaxing them would weaken validation on the
// heavily-used parametric path for no gain.

export const previewCabinetPieceSchema = z.object({
  label: z.string().max(120).optional(),
  kind: cabinetPieceKind.optional(),
  lengthMm: dimMm,
  widthMm: dimMm,
  // Required: a piece with no material cannot be priced. The client filters
  // incomplete rows out of the preview body before sending.
  materialId: z.string().min(1),
  edgeId: z.string().min(1).optional().nullable(),
  edgeCountLength: z.number().int().min(0).max(2).default(0),
  edgeCountWidth: z.number().int().min(0).max(2).default(0),
  doubled: z.boolean().default(false),
  // Deliberately restated rather than shared with `cabinetPieceInput` (see the
  // note above). Leaving it out here would make the live preview under-price
  // every multi-board row relative to the cabinet once saved.
  quantity: z.number().int().min(1).max(10_000).default(1),
  /** Markup for this row's board — per row, not per material. */
  coefficient: nonNegativeDecimal.default('1.00'),
});
export type PreviewCabinetPiece = z.infer<typeof previewCabinetPieceSchema>;

export const previewCabinetPiecesRequestSchema = z.object({
  pieces: z.array(previewCabinetPieceSchema).min(1).max(MAX_CABINET_PIECES),
  hardware: z.array(previewCabinetHardwareItemSchema).default([]),
  quantity: positiveDecimal.default('1'),
});
export type PreviewCabinetPiecesRequest = z.infer<typeof previewCabinetPiecesRequestSchema>;

// ---------------------------------------------------------------------------
// Project-level recompute response
// ---------------------------------------------------------------------------

export const recomputeProjectResponseSchema = z.object({
  projectId: z.string(),
  recomputed: z.number().int().nonnegative(),
  errors: z.array(
    z.object({
      itemType: z.enum(['PANEL', 'COUNTERTOP', 'DRAWER', 'ELEMENT', 'LIGHTING', 'CABINET']),
      itemId: z.string(),
      message: z.string(),
    }),
  ),
});
export type RecomputeProjectResponse = z.infer<typeof recomputeProjectResponseSchema>;

// ---------------------------------------------------------------------------
// Catalog price-impact (PR #14): which projects/items use a catalog entity?
// ---------------------------------------------------------------------------

export const priceImpactProjectSchema = z.object({
  id: z.string(),
  code: z.string(),
  title: z.string(),
  status: z.string(),
  itemCount: z.number().int().nonnegative(),
});
export type PriceImpactProject = z.infer<typeof priceImpactProjectSchema>;

export const priceImpactReportSchema = z.object({
  totalProjects: z.number().int().nonnegative(),
  totalItems: z.number().int().nonnegative(),
  projects: z.array(priceImpactProjectSchema),
});
export type PriceImpactReport = z.infer<typeof priceImpactReportSchema>;

export const recomputeImpactResponseSchema = z.object({
  projectsAffected: z.number().int().nonnegative(),
  totalRecomputed: z.number().int().nonnegative(),
  errors: z.array(
    z.object({
      projectId: z.string(),
      itemType: z.enum(['PANEL', 'COUNTERTOP', 'DRAWER', 'ELEMENT', 'LIGHTING', 'CABINET']),
      itemId: z.string(),
      message: z.string(),
    }),
  ),
});
export type RecomputeImpactResponse = z.infer<typeof recomputeImpactResponseSchema>;

// ---------------------------------------------------------------------------
// Project aggregates: BOM + Time (PR #15)
// ---------------------------------------------------------------------------

export const bomGroupSchema = z.object({
  entityType: z.enum(['material', 'edge', 'hardware', 'lighting', 'drawerVariant']),
  entityId: z.string(),
  name: z.string(),
  offerName: z.string().nullable(),
  manufacturer: z.string().nullable(),
  unit: z.string().nullable(),
  unitPrice: z.string().nullable(),
  currency: z.string().nullable(),
  itemCount: z.number().int().nonnegative(),
  totalCost: z.string(),
  /** Physical quantity consumed across the group, in `unit` (m² / lm / pcs / set). */
  totalQty: z.string(),
  lengthM: z.string().nullable().optional(),
  /**
   * The markup this group was actually sold at, when it has just one.
   *
   * A markup belongs to a USE, not to a catalog row — one board is a 2.5
   * carcass in one cabinet and a 1.0 painted front in the next — so a group
   * that aggregates many uses need not have a single number. This is that
   * number when every use agrees, and null when they do not;
   * `coefficientMin`/`Max` then say how far they spread. Also null when the row
   * carries no quantity at all.
   *
   * It is not an average. The row is editable — `updateBomCoefficientRequest`
   * writes one markup onto every use behind it — and a figure offered for
   * editing has to be one something was really sold at, or typing over it reads
   * as a no-op while re-pricing both halves of a mixed row.
   */
  coefficient: z.string().nullable(),
  coefficientMin: z.string().nullable(),
  coefficientMax: z.string().nullable(),
  /**
   * `totalCost` with each use's own markup applied — Σ `qty × price × coef`,
   * never a rounded summary, so a mixed group still adds up to the ban.
   */
  totalWithCoefficient: z.string(),
});
export type BomGroup = z.infer<typeof bomGroupSchema>;

export const projectBomReportSchema = z.object({
  projectId: z.string(),
  groups: z.array(bomGroupSchema),
  grandTotalCost: z.string(),
  grandTotalWithCoefficient: z.string(),
  currency: z.string(),
});
export type ProjectBomReport = z.infer<typeof projectBomReportSchema>;

// ---------------------------------------------------------------------------
// BOM "Offer Name" inline edit — updates the catalog entity behind a BOM row.
// ---------------------------------------------------------------------------

export const bomOfferNameEntityType = z.enum([
  'material',
  'edge',
  'hardware',
  'lighting',
  'drawerVariant',
]);
export type BomOfferNameEntityType = z.infer<typeof bomOfferNameEntityType>;

export const updateBomOfferNameRequest = z.object({
  entityType: bomOfferNameEntityType,
  entityId: z.string().min(1),
  // Empty string clears the override (stored as null).
  offerName: z.string().trim().max(200),
});
export type UpdateBomOfferNameRequest = z.infer<typeof updateBomOfferNameRequest>;

export const updateBomOfferNameResponse = z.object({
  entityType: bomOfferNameEntityType,
  entityId: z.string(),
  offerName: z.string().nullable(),
});
export type UpdateBomOfferNameResponse = z.infer<typeof updateBomOfferNameResponse>;

// ---------------------------------------------------------------------------
// BOM coefficient inline edit — writes one markup onto every use behind a row.
// ---------------------------------------------------------------------------

/**
 * The BOM rows whose markup can be set.
 *
 * `edge` is absent: banding has no markup of its own anywhere in the schema —
 * it is always sold at the markup of the board it wraps (see the drawer and
 * cabinet calculators), so an edge row follows its material and cannot be
 * given a figure independently.
 */
export const bomCoefficientEntityType = z.enum([
  'material',
  'hardware',
  'lighting',
  'drawerVariant',
]);
export type BomCoefficientEntityType = z.infer<typeof bomCoefficientEntityType>;

export const updateBomCoefficientRequest = z.object({
  entityType: bomCoefficientEntityType,
  entityId: z.string().min(1),
  /**
   * The markup to record against every use of this item in scope.
   *
   * Written onto the uses, not onto the catalog row: the catalog default is
   * what a NEW use starts from and changing it is deliberately not retroactive.
   */
  coefficient: nonNegativeDecimal,
  /**
   * Narrows the write to one room, matching the BOM the user is looking at.
   * Omitted, every use in the project is set.
   */
  roomId: z.string().cuid().optional(),
});
export type UpdateBomCoefficientRequest = z.infer<typeof updateBomCoefficientRequest>;

export const updateBomCoefficientResponse = z.object({
  entityType: bomCoefficientEntityType,
  entityId: z.string(),
  coefficient: z.string(),
  /** How many uses were written — line items, cut-list rows, fittings. */
  updatedUses: z.number().int().nonnegative(),
  /**
   * Uses the markup could not be written to, and why.
   *
   * Empty in the ordinary case. It exists because a row can aggregate a use
   * whose markup is not its own to give: nothing qualifies today, but the
   * field keeps a future exception visible to the user instead of silently
   * leaving part of a row un-normalized.
   */
  skipped: z
    .array(z.object({ reason: z.string(), count: z.number().int().positive() }))
    .default([]),
});
export type UpdateBomCoefficientResponse = z.infer<typeof updateBomCoefficientResponse>;

// ---------------------------------------------------------------------------
// BOM "edit material": swap one board for another across chosen uses.
// ---------------------------------------------------------------------------

/** The line-item kinds a BOM material row is made of (elements carry 0 m²). */
export const bomMaterialUseKind = z.enum(['cabinet', 'drawer', 'panel', 'countertop']);
export type BomMaterialUseKind = z.infer<typeof bomMaterialUseKind>;

/**
 * Where on the item the board sits. Cabinets: carcass/front/back, or `pieces`
 * for a cut-list cabinet (every row on the board at once). Drawers: `box`
 * (sides, back, bottom) and `front`. Panels and countertops have one board.
 */
export const bomMaterialUseSlot = z.enum(['carcass', 'front', 'back', 'pieces', 'box', 'material']);
export type BomMaterialUseSlot = z.infer<typeof bomMaterialUseSlot>;

export const BOM_MATERIAL_SLOTS_BY_KIND: Record<BomMaterialUseKind, readonly BomMaterialUseSlot[]> =
  {
    cabinet: ['carcass', 'front', 'back', 'pieces'],
    drawer: ['box', 'front'],
    panel: ['material'],
    countertop: ['material'],
  };

export const bomEdgeRef = z.object({ id: z.string(), name: z.string() });
export type BomEdgeRef = z.infer<typeof bomEdgeRef>;

/**
 * What a swap does to the slot's edge banding, decided by the same rule the
 * item dialogs apply when a material is picked (`chooseEdgeForMaterial`):
 *
 *   - `auto`         — the rule picked an edge (two for a cut list mixing single
 *                      and doubled boards, which want different sizes);
 *   - `none`         — painted MDF: the banding is removed;
 *   - `unchanged`    — this slot's banding does not follow its board (a drawer
 *                      box whose banding follows its front; a back with none);
 *   - `incompatible` — the rule found nothing, so the CURRENT edge stays and is
 *                      flagged: the board has no manufacturer, or no edge in
 *                      the catalogue matches it.
 */
export const bomEdgeOutcome = z.discriminatedUnion('status', [
  z.object({ status: z.literal('auto'), edges: z.array(bomEdgeRef).min(1) }),
  z.object({ status: z.literal('none') }),
  z.object({ status: z.literal('unchanged') }),
  z.object({
    status: z.literal('incompatible'),
    reason: z.enum(['noManufacturer', 'noMatch']),
  }),
]);
export type BomEdgeOutcome = z.infer<typeof bomEdgeOutcome>;

export const bomMaterialRef = materialListItem.pick({
  id: true,
  name: true,
  sku: true,
  structure: true,
  thicknessMm: true,
  category: true,
  manufacturer: true,
  coefficient: true,
});
export type BomMaterialRef = z.infer<typeof bomMaterialRef>;

/** One use of the board: an item and the slot on it. */
export const bomMaterialUse = z.object({
  kind: bomMaterialUseKind,
  id: z.string(),
  slot: bomMaterialUseSlot,
  name: z.string(),
  roomName: z.string().nullable(),
  areaName: z.string().nullable(),
  /** Drawers only: the cabinet the drawer belongs to. */
  cabinetName: z.string().nullable(),
  /** MANUAL rows keep their typed price — the swap cannot re-price them. */
  pricingMode,
  /** `pieces` only: how many cut-list rows sit on the board. */
  pieceCount: z.number().int().nullable(),
  /** The banding on the slot now; several on a cut list, empty for none. */
  currentEdges: z.array(bomEdgeRef),
  /** Present only while a replacement board is being previewed. */
  newEdge: bomEdgeOutcome.optional(),
});
export type BomMaterialUse = z.infer<typeof bomMaterialUse>;

export const bomMaterialUsesQuery = z.object({
  materialId: z.string().cuid(),
  /** Matches the BOM on screen: a room BOM lists that room's uses only. */
  roomId: z.string().cuid().optional(),
  /** Preview the edge outcome of swapping to this board. */
  toMaterialId: z.string().cuid().optional(),
});
export type BomMaterialUsesQuery = z.infer<typeof bomMaterialUsesQuery>;

export const bomMaterialUsesResponse = z.object({
  material: bomMaterialRef,
  replacement: bomMaterialRef.nullable(),
  uses: z.array(bomMaterialUse),
});
export type BomMaterialUsesResponse = z.infer<typeof bomMaterialUsesResponse>;

export const bomMaterialSwapRequest = z
  .object({
    fromMaterialId: z.string().cuid(),
    toMaterialId: z.string().cuid(),
    roomId: z.string().cuid().optional(),
    uses: z
      .array(
        z.object({ kind: bomMaterialUseKind, id: z.string().cuid(), slot: bomMaterialUseSlot }),
      )
      .min(1)
      .max(2000),
  })
  .refine((v) => v.fromMaterialId !== v.toMaterialId, {
    message: 'The replacement must be a different material.',
    path: ['toMaterialId'],
  })
  .superRefine((v, ctx) => {
    v.uses.forEach((use, index) => {
      if (!BOM_MATERIAL_SLOTS_BY_KIND[use.kind].includes(use.slot)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `A ${use.kind} has no ${use.slot} slot.`,
          path: ['uses', index, 'slot'],
        });
      }
    });
  });
export type BomMaterialSwapRequest = z.infer<typeof bomMaterialSwapRequest>;

export const bomMaterialSwapResponse = z.object({
  /** Slots that took the new board (a cut list counts once per cabinet). */
  updatedUses: z.number().int().nonnegative(),
  edges: z.object({
    /** Slots whose banding the rule replaced. */
    changed: z.number().int().nonnegative(),
    /** Slots whose banding was removed (painted MDF). */
    cleared: z.number().int().nonnegative(),
    /** Slots that kept an edge the rule could not match to the new board. */
    incompatible: z.number().int().nonnegative(),
  }),
  /** Uses that no longer sit on the source board, or no longer exist. */
  skipped: z.array(
    z.object({
      kind: bomMaterialUseKind,
      id: z.string(),
      slot: bomMaterialUseSlot,
      reason: z.enum(['notFound', 'stale']),
    }),
  ),
  /** The project re-pricing that ran in the same transaction. */
  recompute: recomputeProjectResponseSchema.pick({ recomputed: true, errors: true }),
});
export type BomMaterialSwapResponse = z.infer<typeof bomMaterialSwapResponse>;

export const projectTimeReportSchema = z.object({
  projectId: z.string(),
  itemsContributing: z.number().int().nonnegative(),
  itemsTotal: z.number().int().nonnegative(),
  totalsSec: z.object({
    circularSec: z.number(),
    cantSec: z.number(),
    cncSec: z.number(),
    assemblySec: z.number(),
    holesSec: z.number(),
    totalSec: z.number(),
  }),
  totalsHours: z.object({
    circular: z.string(),
    cant: z.string(),
    cnc: z.string(),
    assembly: z.string(),
    holes: z.string(),
    total: z.string(),
  }),
});
export type ProjectTimeReport = z.infer<typeof projectTimeReportSchema>;
